import { useEffect, useState } from 'react';
import { CalendarClock, Clock, UserCheck } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { RepWeeklyHoursDrawer } from './RepWeeklyHoursDrawer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { formatUserName } from '@/lib/leads/leadFormat';
import {
  useLeadRepAvailability,
  type LeadRepAvailability,
  type LeadRepUnavailableReason,
} from '@/hooks/leads/useLeadRepAvailability';
import { useUpdateLeadRepAvailability } from '@/hooks/leads/useUpdateLeadRepAvailability';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';

const REASON_OPTIONS: { value: LeadRepUnavailableReason; label: string }[] = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'PTO', label: 'PTO' },
  { value: 'OFF_SHIFT', label: 'Off shift' },
];

const REASON_LABEL: Record<LeadRepUnavailableReason, string> = {
  MANUAL: 'Manual',
  PTO: 'PTO',
  OFF_SHIFT: 'Off shift',
};

/** Convert an ISO string to the `yyyy-MM-ddThh:mm` shape a datetime-local input wants. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
}

/**
 * Debounced datetime-local cell: holds a local draft so typing/picking doesn't
 * fire a PUT per keystroke, and commits to the API on blur only when changed.
 */
function UntilInput({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (localValue: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Re-sync when the persisted value changes (e.g. after a refetch) so the
  // input reflects server state when we're not mid-edit.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <Input
      type="datetime-local"
      className="h-8"
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== value) onCommit(draft);
      }}
    />
  );
}

export function LeadRepAvailabilitySection({ canConfigure }: { canConfigure: boolean }) {
  const availabilityQuery = useLeadRepAvailability();
  const updateAvailability = useUpdateLeadRepAvailability();
  const companyTimezone = useCompanyTimezone();
  // Track which row is mid-flight to disable only that row's controls.
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  // The rep whose weekly hours are being edited in the drawer.
  const [hoursEditing, setHoursEditing] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  const reps = availabilityQuery.data ?? [];

  function mutate(
    rep: LeadRepAvailability,
    patch: {
      isAvailable: boolean;
      unavailableReason?: LeadRepUnavailableReason;
      unavailableUntil?: string;
    },
  ) {
    setPendingUserId(rep.userId);
    updateAvailability.mutate(
      { userId: rep.userId, ...patch },
      { onSettled: () => setPendingUserId(null) },
    );
  }

  function handleToggle(rep: LeadRepAvailability, nextAvailable: boolean) {
    if (nextAvailable) {
      mutate(rep, { isAvailable: true });
      return;
    }
    // Going unavailable — default to MANUAL, preserving any existing "until".
    mutate(rep, {
      isAvailable: false,
      unavailableReason: rep.unavailableReason ?? 'MANUAL',
      ...(rep.unavailableUntil ? { unavailableUntil: rep.unavailableUntil } : {}),
    });
  }

  function handleReasonChange(rep: LeadRepAvailability, reason: LeadRepUnavailableReason) {
    mutate(rep, {
      isAvailable: false,
      unavailableReason: reason,
      ...(rep.unavailableUntil ? { unavailableUntil: rep.unavailableUntil } : {}),
    });
  }

  function handleUntilChange(rep: LeadRepAvailability, localValue: string) {
    mutate(rep, {
      isAvailable: false,
      unavailableReason: rep.unavailableReason ?? 'MANUAL',
      ...(localValue ? { unavailableUntil: new Date(localValue).toISOString() } : {}),
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </span>
          Rep Availability
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Mark reps unavailable so the assignment engine skips them while they're out.
        </p>
      </CardHeader>
      <CardContent>
        {availabilityQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : reps.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No reps found.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rep</TableHead>
                  <TableHead className="w-32 text-center">Available</TableHead>
                  <TableHead className="w-40">Reason</TableHead>
                  <TableHead className="w-56">Until</TableHead>
                  <TableHead className="w-28 text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reps.map((rep) => {
                  const rowPending = pendingUserId === rep.userId;
                  return (
                    <TableRow key={rep.userId}>
                      <TableCell>
                        <p className="font-medium">{formatUserName(rep, rep.email)}</p>
                        <p className="text-xs text-muted-foreground">{rep.email}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            checked={rep.isAvailable}
                            disabled={!canConfigure || rowPending}
                            onCheckedChange={(checked) => handleToggle(rep, checked)}
                            aria-label={`Toggle availability for ${formatUserName(rep, rep.email)}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {rep.isAvailable ? (
                          <Badge variant="outline" className="text-emerald-700">
                            Available
                          </Badge>
                        ) : canConfigure ? (
                          <Select
                            value={rep.unavailableReason ?? 'MANUAL'}
                            onValueChange={(value) =>
                              handleReasonChange(rep, value as LeadRepUnavailableReason)
                            }
                            disabled={rowPending}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {REASON_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {rep.unavailableReason ? REASON_LABEL[rep.unavailableReason] : '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {rep.isAvailable ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : canConfigure ? (
                          <UntilInput
                            value={toLocalInput(rep.unavailableUntil)}
                            disabled={rowPending}
                            onCommit={(value) => handleUntilChange(rep, value)}
                          />
                        ) : rep.unavailableUntil ? (
                          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <CalendarClock size={13} />
                            {formatDateTimeInTimeZone(rep.unavailableUntil, companyTimezone, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Indefinite</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() =>
                            setHoursEditing({
                              userId: rep.userId,
                              name: formatUserName(rep, rep.email),
                            })
                          }
                        >
                          <Clock size={13} className="mr-1" />
                          Edit hours
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <RepWeeklyHoursDrawer
        userId={hoursEditing?.userId ?? null}
        userName={hoursEditing?.name ?? ''}
        canConfigure={canConfigure}
        onClose={() => setHoursEditing(null)}
      />
    </Card>
  );
}
